import { getCachedPushToken } from "../api/auth/pushToken";

declare global {
  interface Navigator {
    /** Present on iOS Safari / WKWebView; undefined on desktop Mac. */
    standalone?: boolean;
  }
}

/**
 * Detects iPhone / iPad / iPod, including iPadOS desktop-mode Safari
 * (which spoofs Macintosh) and the Flutter iOS WebView.
 *
 * Note: do not use `"ontouchend" in document` with a Mac UA — desktop Chrome
 * exposes that property and would false-positive real Macs.
 */
export const isIOSDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  // Classic mobile UA / platform (Chrome on iPad reports platform "iPad").
  if (/iPad|iPhone|iPod/i.test(ua) || /iPad|iPhone|iPod/i.test(platform)) {
    return true;
  }

  // iPadOS 13+ "Request Desktop Website": UA looks like Mac. Prefer UA over
  // navigator.platform (deprecated / often empty).
  // Real Macs report maxTouchPoints === 0; real iPads report 5+.
  // Chrome DevTools iPad emulation reports 1 — use >= 1 so both are covered.
  const looksLikeMac =
    /Macintosh|Mac OS X/i.test(ua) || /MacIntel|MacPPC|Mac68K/i.test(platform);
  if (
    looksLikeMac &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints >= 1
  ) {
    return true;
  }

  // iOS-only API: defined on Safari / WKWebView (even when not installed to home screen).
  if (typeof navigator.standalone === "boolean") {
    return true;
  }

  // Flutter iOS WebView may report platform via push token.
  if (getCachedPushToken()?.platform === "ios") {
    return true;
  }

  // Flutter WKWebView bridge (Android uses a different channel, not webkit handlers).
  const flutterWebView =
    (window as Window & { __FLUTTER_WEBVIEW__?: boolean }).__FLUTTER_WEBVIEW__ ===
    true;
  if (flutterWebView) {
    const webkit = (
      window as Window & {
        webkit?: { messageHandlers?: Record<string, unknown> };
      }
    ).webkit;
    if (
      webkit?.messageHandlers &&
      Object.keys(webkit.messageHandlers).length > 0
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Shortest viewport side in CSS pixels. iPhones stay under ~440 even in
 * landscape; iPad Mini 6 is 744 in portrait.
 */
export const IOS_TABLET_MIN_SHORT_SIDE_PX = 700;

const getUserAgentAndPlatform = (): { ua: string; platform: string } => {
  if (typeof navigator === "undefined") {
    return { ua: "", platform: "" };
  }
  return {
    ua: navigator.userAgent || "",
    platform: navigator.platform || "",
  };
};

/**
 * True for iPad / iPadOS, including desktop-mode Safari (Macintosh + touch).
 */
export const isIPadDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const { ua, platform } = getUserAgentAndPlatform();

  if (/iPad/i.test(ua) || /iPad/i.test(platform)) {
    return true;
  }

  const looksLikeMac =
    /Macintosh|Mac OS X/i.test(ua) || /MacIntel|MacPPC|Mac68K/i.test(platform);
  return (
    looksLikeMac &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints >= 1
  );
};

export const isTabletSizedViewport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    Math.min(window.innerWidth, window.innerHeight) >=
    IOS_TABLET_MIN_SHORT_SIDE_PX
  );
};

/**
 * App Store IAP rules apply to iPhone. Keep web checkout hidden there,
 * but allow it on iPad and other large iOS screens.
 */
export const shouldHideSubscriptions = (): boolean => {
  if (!isIOSDevice()) {
    return false;
  }
  if (isIPadDevice() || isTabletSizedViewport()) {
    return false;
  }
  return true;
};
